'use client';

import React from 'react';
import { styled } from '@linaria/react';

import { COLOR_SWAP_TRANSITION } from '@/constants';
import useWindowDimensions from '@/hooks/use-window-dimensions';
import { random, normalize } from '@/utils';

import { useColorMode } from '@/components/UserPreferencesProvider';

import SHAPES, { Shape } from './WhimsyShapes.data';

const SPRITE_SRC_LIGHT = '/images/home/wham-shapes-light-final.png';
const SPRITE_SRC_DARK = '/images/home/wham-shapes-dark-final.png';

const WhimsyShapes = React.memo(function WhimsyShapes() {
  const { width: windowWidth } = useWindowDimensions();
  const isMobile =
    typeof windowWidth === 'number' && windowWidth <= 768;

  const hasLoaded = useHasSpriteLoaded();

  const colorMode = useColorMode();

  if (!hasLoaded) {
    return null;
  }

  return (
    <Wrapper data-has-loaded={hasLoaded}>
      {SHAPES.map((shape) => {
        const { translateX, translateY } = getShapePosition(shape, {
          isMobile,
        });

        const enterFadeDuration = normalize(
          shape.fadeScale ?? 0,
          0,
          1,
          400,
          600
        );
        const enterFadeDelay = normalize(
          shape.fadeScale ?? 0,
          0,
          1,
          0,
          300
        );

        return (
          <ImgWrapper
            key={shape.id}
            data-shape-id={shape.id}
            style={{
              // Styles related to the element’s original position:
              top: shape.verticalAnchor === 'bottom' ? undefined : 0,
              bottom:
                shape.verticalAnchor === 'bottom' ? 0 : undefined,
              filter: shape.filter,
              '--width': `${shape.width}px`,
              '--height': `${shape.height}px`,
              '--object-position': `-${shape.x}px -${shape.y}px`,
              transform: `
                  translate(
                    ${translateX / 16}rem,
                    ${translateY / 16}rem
                  )
                  scale(0.5)`,

              // Styles related to the element’s enter animation:
              '--enter-fade-duration': `${enterFadeDuration}ms`,
              '--enter-fade-delay': `${enterFadeDelay}ms`,
            }}
          >
            <Layer src={SPRITE_SRC_DARK} alt="" />
            <LightLayerWrapper
              style={{ opacity: colorMode === 'light' ? 1 : 0 }}
            >
              <Layer src={SPRITE_SRC_LIGHT} alt="" />
            </LightLayerWrapper>
          </ImgWrapper>
        );
      })}
    </Wrapper>
  );
});

function useHasSpriteLoaded() {
  const [hasLoaded, setHasLoaded] = React.useState(false);

  // In order to smoothly transition between light/dark shapes, we need to load *both* and stack them. There is also a sequential fade animation, and for that to work, we need to wait until they’re both loaded.
  React.useEffect(() => {
    const start = Date.now();
    let hasLightLoaded = false;
    let hasDarkLoaded = false;

    function handleHasLoadedEverything() {
      const loadTime = Date.now() - start;

      if (loadTime <= 10_000) {
        setHasLoaded(true);
      }
    }

    const lightImg = new Image();
    lightImg.src = SPRITE_SRC_LIGHT;
    lightImg.onload = () => {
      hasLightLoaded = true;
      if (hasDarkLoaded) {
        handleHasLoadedEverything();
      }
    };

    const darkImg = new Image();
    darkImg.src = SPRITE_SRC_DARK;
    darkImg.onload = () => {
      hasDarkLoaded = true;
      if (hasLightLoaded) {
        handleHasLoadedEverything();
      }
    };
  }, []);

  return hasLoaded;
}

function getShapePosition(
  shape: Shape,
  {
    isMobile,
  }: {
    isMobile: boolean;
  }
) {
  const baseTranslateX = isMobile
    ? shape.mobile?.translateX ?? shape.translateX
    : shape.translateX;
  const baseTranslateY = isMobile
    ? shape.mobile?.translateY ?? shape.translateY
    : shape.translateY;

  return {
    translateX: baseTranslateX,
    translateY: baseTranslateY,
  };
}

const Wrapper = styled.div`
  position: absolute;
  inset: 0;
  overflow: hidden;
  overflow: clip;
  transition: opacity 300ms ease-in-out;
  pointer-events: none;
`;

const ImgWrapper = styled.div`
  /* Overwrite the "relative" position from setting data-mass */
  position: absolute !important;
  left: 50%;
  width: var(--width);
  height: var(--height);
  transform-origin: top left;
  transform: var(--transform);
  user-select: none;
  animation: fadeFromTransparent var(--enter-fade-duration)
    var(--enter-fade-delay) backwards cubic-bezier(0.3, 0, 0.15, 1);
`;

const LightLayerWrapper = styled.div`
  position: absolute;
  inset: 0;
  transition: opacity ${COLOR_SWAP_TRANSITION.duration}ms
    ${COLOR_SWAP_TRANSITION.timingFunction};
`;

const Layer = styled.img`
  display: block;
  width: 100%;
  height: 100%;
  object-fit: none;
  object-position: var(--object-position);
`;

export default WhimsyShapes;
